import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Typography,
  Button,
  Tabs,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
} from "@mui/material";
import { rowsPerPage } from "../../../Constants";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExtensionIcon from "@mui/icons-material/Extension";
import { useSelector, useDispatch } from "react-redux";
import Pagination from "@mui/material/Pagination";
import {
  deleteTag,
  editTag,
} from "../../../redux/slices/orgAdmin/tagsOrgSlice";
import {
  StyledButton,
  StyledContainer,
  StyledTableCell,
} from "../../CSS/Styles";
import {
  listTags,
  saveTags,
  listItems,
} from "../../../redux/slices/orgAdmin/tagsOrgSlice";

export default function Tags() {
  const dispatch = useDispatch();
  const { tags, items, loading } = useSelector((state) => state.tags);
  const { companyId } = useSelector((state) => state.app);
  const { languagesList } = useSelector((state) => state.languages); // Dynamic languages
  const [deleteTitle, setDeleteTitle] = useState("");
  const [tagTitle, setTagTitle] = useState("");

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [promotionIdToDelete, setPromotionIdToDelete] = useState(null);
  const [tagId, setTagId] = useState(null);
  const [rowToEdit, setRowToEdit] = useState(null);
  const totalRecords = items?.pagination?.totalRecords;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(listTags({ companyId }));
  }, [dispatch, companyId]);

  const [isAddingCategory, setIsAddingCategory] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(
    languagesList?.[0]?.code || "en"
  );
  const [newCategory, setNewCategory] = useState({});

  useEffect(() => {
    const initialCategoryState = languagesList.reduce((acc, lang) => {
      acc[lang.code] = { tag_name: "", tag_description: "" };
      return acc;
    }, {});
    setNewCategory(initialCategoryState);
  }, [languagesList]);

  const handleTabChange = (event, newLang) => {
    setCurrentLanguage(newLang);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleInputChange = (lang, field, value) => {
    setNewCategory((prev) => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  const handleEditCategory = async (categoryId) => {
    setRowToEdit(categoryId.id);
    try {
      const categoryData = await dispatch(editTag({ id: categoryId.id })).then(
        (res) => res.payload
      );

      if (!categoryData || !categoryData.languages) {
        console.error("No languages found in API response.");
        return;
      }

      if (!languagesList || languagesList.length === 0) {
        console.error("languagesList is empty or undefined.");
        return;
      }

      const prefilledCategory = languagesList.reduce((acc, lang) => {
        acc[lang.code] = {
          tag_name: categoryData.languages?.[lang.code]?.tag_name || "",
          tag_description:
            categoryData.languages?.[lang.code]?.tag_description || "",
        };
        return acc;
      }, {});

      setNewCategory(prefilledCategory);

      setCurrentLanguage(currentLanguage);

      setIsAddingCategory(true);
    } catch (error) {
      console.error("Error fetching category data:", error);
    }
  };

  const handleAddCategory = () => {
    setRowToEdit(null);
    const emptyCategory = languagesList.reduce((acc, lang) => {
      acc[lang.code] = { tag_name: "", tag_description: "" };
      return acc;
    }, {});
    setNewCategory(emptyCategory);
    setIsAddingCategory(true);
  };

  const handleSaveCategory = () => {
    const payload = rowToEdit ? { id: rowToEdit } : {};

    Object.entries(newCategory).forEach(([lang, data]) => {
      if (["en", "fr", "ar"].includes(lang)) {
        payload[lang] = {
          tag_name: data.tag_name?.trim() || "",
          tag_description: data.tag_description?.trim() || "",
        };
      }
    });

    dispatch(saveTags({ categoryData: payload })).then(() => {
      dispatch(listTags({ companyId }));
      setCurrentLanguage(languagesList?.[0]?.code || "en");
    });

    setIsAddingCategory(false);
  };

  const handleDeleteClick = (id, title) => {
    setPromotionIdToDelete(id);
    setDeleteDialogOpen(true);
    setDeleteTitle(title);
  };
  const handleTagsClick = (id, title) => {
    setTagsDialogOpen(true);
    setTagId(id);
    setTagTitle(title);
    dispatch(listTags({ companyId }));

    dispatch(listItems({ companyId, id, page, limit: rowsPerPage }));
  };

  const confirmDelete = () => {
    dispatch(deleteTag({ id: promotionIdToDelete }))
      .then(() => {
        setDeleteDialogOpen(false);
        setPromotionIdToDelete(null);

        // Fetch updated promotions list after successful deletion
        dispatch(listTags({ companyId }));
      })
      .catch((error) => {
        console.error("Error deleting promotion:", error);
      });
  };

  useEffect(() => {
    if (!tagId) return;
    dispatch(listItems({ companyId, id: tagId, page, limit: rowsPerPage }));
  }, [dispatch, page]);

  return (
    <StyledContainer>
      <Paper elevation={3} sx={{ padding: 3 }}>
        {!isAddingCategory ? (
          <>
            <Typography variant="h5" sx={{ mb: 2 }}>
              Tags
            </Typography>
            <StyledButton
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddCategory}
              sx={{ marginBottom: 2 }}
              disabled={loading}
            >
              Add
            </StyledButton>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Tag Name</StyledTableCell>
                    <StyledTableCell>Tag Description</StyledTableCell>
                    <StyledTableCell>Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {
                    tags
                      ?.map((item) => {
                        return (
                          <TableRow key={item.id}>
                            <TableCell>{item.tag_name || "-"}</TableCell>
                            <TableCell>{item.tag_description || "-"}</TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => handleEditCategory(item)}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                onClick={() =>
                                  handleDeleteClick(item.id, item.tag_name)
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                              <IconButton
                                onClick={() =>
                                  handleTagsClick(item.id, item.tag_name)
                                }
                              >
                                <ExtensionIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })
                      .filter(Boolean) // Remove null rows
                  }
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <IconButton
                onClick={() => {
                  setIsAddingCategory(false);
                  setCurrentLanguage(languagesList?.[0]?.code || "en");
                }}
              >
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6">Tag</Typography>
            </Box>
            {languagesList?.length > 1 && (
              <Tabs value={currentLanguage} onChange={handleTabChange}>
                {languagesList.map((lang) => (
                  <Tab key={lang.code} label={lang.name} value={lang.code} />
                ))}
              </Tabs>
            )}

            <TextField
              sx={{ mt: 2 }}
              label="Tag Name"
              value={newCategory[currentLanguage]?.tag_name || ""}
              onChange={(e) =>
                handleInputChange(currentLanguage, "tag_name", e.target.value)
              }
              fullWidth
            />

            <TextField
              sx={{ mt: 2 }}
              label="Tag Description"
              value={newCategory[currentLanguage]?.tag_description || ""}
              onChange={(e) =>
                handleInputChange(
                  currentLanguage,
                  "tag_description",
                  e.target.value
                )
              }
              fullWidth
            />

            <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={() => {
                  setIsAddingCategory(false);
                  setCurrentLanguage(languagesList?.[0]?.code || "en");
                }}
                sx={{ marginRight: 2 }}
                color="error"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveCategory}
                color="primary"
                variant="contained"
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete{" "}
              <span style={{ fontWeight: "bold" }}> {deleteTitle}</span>? This
              action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button color="error" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={tagsDialogOpen}
          onClose={() => setTagsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Items associated with{" "}
            <span style={{ fontWeight: "bold" }}>{tagTitle}</span>
          </DialogTitle>
          <DialogContent>
            {items?.data?.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                        Section
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                        Title
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items?.data?.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.content_type || "-"}</TableCell>
                        <TableCell>
                          <strong>{item.title || "-"}</strong>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography sx={{ mt: 2 }}>
                No items associated to this tag.
              </Typography>
            )}
            <Box mt={2} display="flex" justifyContent="center">
              {items?.data?.length > 0 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                />
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTagsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </StyledContainer>
  );
}
